/**
 * `<MoveUnderPicker/>` — choose a new parent for a task (round-4 ⑥, #1043).
 *
 * Round-4 ⑥ ships freeform drag-to-pin on the canvas, which conflicts with the
 * old "drag a card onto another to re-parent" gesture. The cleanest split is
 * to keep plain card-drag for hand-positioning (drag-to-pin), and route the
 * re-parent action through an explicit menu — the card's `⋯` overflow opens
 * this small picker, which lists every other task in the team that this node
 * could be moved under (the node itself + its descendants are excluded; the
 * cycle guard belongs to the parent's `reparent` handler).
 *
 * Brand firewall (§A9): re-parenting is a calm structural choice about the
 * WORK's hierarchy, never a fact about a person. Copy is factual ("Move under
 * a task", "(no parent — outcome)").
 *
 * Reuse: this picker shares the anchored-dropdown geometry + search-then-list
 * shape with `<AssignPopover/>` (the wireframe vocabulary the editorial Map
 * already uses), so the lead gets a consistent affordance across surfaces.
 */

import { useEffect, useMemo, useRef, useState } from "react";

import { useMapTheme } from "../theme/MapThemeContext";

export interface MoveUnderCandidate {
  /** The node id we'd reparent under (a task in this team's tree). */
  id: string;
  /** The candidate's title — what the row shows. */
  title: string;
  /** Depth from the root (0 = root); informs the indent in the list. */
  depth: number;
}

export interface MoveUnderPickerProps {
  /** The node we're moving — its title appears in the header. */
  nodeTitle: string;
  /**
   * Eligible parents: every task in the team's tree EXCEPT the moving node
   * itself, its descendants (would create a cycle), and `link_block`s
   * (link_blocks never become parents — they're off-tree). The caller filters
   * the list; this component just renders + searches it.
   */
  candidates: MoveUnderCandidate[];
  /**
   * The current parent id (so it can be marked as "current parent" in the
   * list and the call is a no-op when re-picked). Null ⇒ this node is a root.
   */
  currentParentId: string | null;
  /**
   * Whether to offer "(no parent — outcome)" as a top-level pick. True only
   * for nodes whose move-to-root would not create a re-rooting issue: today
   * we always allow it (the service handles cycle + permission). The lead can
   * promote any task to a root via this row.
   */
  allowRoot?: boolean;
  /** Pick a candidate by id (null ⇒ "(no parent — outcome)" / promote to root). */
  onPick: (parentId: string | null) => void;
  /** Dismiss the popover (Esc / click-out). */
  onClose: () => void;
}

export function MoveUnderPicker({
  nodeTitle,
  candidates,
  currentParentId,
  allowRoot = true,
  onPick,
  onClose,
}: MoveUnderPickerProps) {
  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";

  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  // Esc dismisses; click-out is handled by the parent's outside-click ring
  // (the same pattern the assign popover uses).
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter((cn) => cn.title.toLowerCase().includes(q));
  }, [candidates, query]);

  return (
    <div
      data-testid="move-under-picker"
      className="absolute right-0 z-30 mt-1 w-64 rounded-md border bg-white p-2 shadow-lg"
      style={{ borderColor: c.softBorder }}
      role="menu"
    >
      <div className="px-1 pb-1.5">
        <p
          className="truncate text-[11px]"
          style={{
            color: c.muted,
            fontFamily: editorial ? theme.fonts.mono : undefined,
            letterSpacing: editorial ? "0.08em" : undefined,
            textTransform: editorial ? "uppercase" : undefined,
          }}
        >
          Move "{nodeTitle}" under…
        </p>
      </div>
      <input
        ref={searchRef}
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Find a task"
        data-testid="move-under-search"
        className="mb-1 w-full rounded border px-2 py-1 text-xs outline-none"
        style={{ borderColor: c.softBorder, color: c.ink }}
      />
      <div className="max-h-48 overflow-y-auto">
        {allowRoot && (
          <button
            type="button"
            data-testid="move-under-row-root"
            onClick={() => onPick(null)}
            className="block w-full rounded px-2 py-1 text-left text-xs transition-colors hover:bg-slate-50"
            style={{ color: currentParentId === null ? c.muted : c.ink }}
          >
            (no parent — outcome){currentParentId === null ? " · current" : ""}
          </button>
        )}
        {filtered.length === 0 && (
          <p
            className="px-2 py-1 text-[11px]"
            style={{ color: c.muted }}
            data-testid="move-under-empty"
          >
            No matches.
          </p>
        )}
        {filtered.map((cn) => {
          const isCurrent = cn.id === currentParentId;
          return (
            <button
              key={cn.id}
              type="button"
              data-testid={`move-under-row-${cn.id}`}
              onClick={() => onPick(cn.id)}
              className="block w-full truncate rounded px-2 py-1 text-left text-xs transition-colors hover:bg-slate-50"
              style={{
                color: isCurrent ? c.muted : c.ink,
                paddingLeft: 8 + Math.min(cn.depth, 4) * 10,
              }}
            >
              {cn.title}
              {isCurrent ? " · current" : ""}
            </button>
          );
        })}
      </div>
    </div>
  );
}
